// Non-muscle anatomical structure taxonomy for pain capture
// (PAIN_TRACKING_PLAN.md Phase 1, D-PAIN-5).
//
// A PARALLEL layer to the existing 53-muscle taxonomy (shared/src/muscleMap.ts),
// which is left completely untouched (D-PAIN-5, same isolation rule D-BM-4
// established for the Trainer balance math). Joints, spine segments, bone
// landmarks, peripheral nerves, dermatome bands, visceral quadrants and soft
// tissue live here and only here.
//
// `blurb` is one plain-English sentence of CLINICAL context (what the structure
// is, where pain from it commonly refers) — deliberately unlike
// MUSCLE_META.blurb, which is training-focused.
//
// `views` lists only the panels where the structure will actually get a region
// path. Session 2 authors front/back/side; Sessions 3-5 author the six detail
// panels and APPEND their view names to the relevant `views` arrays here.
//
// Dermatome / nerve regions are educational overlays, not diagnostic — they feed
// hedged AI narrative only (CLAUDE.md: "not a medical device").
//
// Pure data — React-free / JSX-free.

import type { PainStructureKind, PainView } from "./painMap.js";

export const PAIN_STRUCTURES = [
  // joints
  "joint-shoulder",
  "joint-elbow",
  "joint-wrist",
  "joint-hip",
  "joint-knee",
  "joint-ankle",
  "joint-tmj",
  "joint-si",
  "joint-mcp",
  "joint-pip",
  "joint-dip",
  "joint-mtp",
  "joint-subtalar",
  // spine segments
  "spine-c",
  "spine-t",
  "spine-l1",
  "spine-l2",
  "spine-l3",
  "spine-l4",
  "spine-l5",
  "spine-s",
  // bone landmarks
  "bone-clavicle",
  "bone-sternum",
  "bone-rib",
  "bone-patella",
  "bone-tibia-shaft",
  "bone-calcaneus",
  "bone-metatarsal",
  "bone-scaphoid",
  // nerves
  "nerve-sciatic",
  "nerve-median",
  "nerve-ulnar",
  "nerve-radial",
  "nerve-peroneal",
  "nerve-tibial",
  "nerve-plantar",
  // dermatomes (educational overlay only)
  "derm-c5",
  "derm-c6",
  "derm-c7",
  "derm-c8",
  "derm-t1",
  "derm-l4",
  "derm-l5",
  "derm-s1",
  // visceral quadrants
  "visc-ruq",
  "visc-luq",
  "visc-rlq",
  "visc-llq",
  "visc-epigastric",
  "visc-suprapubic",
  // soft tissue / other
  "fascia-plantar",
  "tissue-skin",
  "tissue-tendon",
] as const;
export type PainStructure = (typeof PAIN_STRUCTURES)[number];

export interface PainStructureMeta {
  /** Formal anatomical name, e.g. "Glenohumeral Joint". */
  label: string;
  /** Everyday name, e.g. "Shoulder Joint". */
  common: string;
  /** One plain-English sentence of clinical context (not training context). */
  blurb: string;
  kind: PainStructureKind;
  /** Panels where this structure will get a region path. Sessions 3-5 append. */
  views: PainView[];
  /** True when the structure is authored once for the left side and mirrored. */
  paired: boolean;
}

export const PAIN_STRUCTURE_META: Record<PainStructure, PainStructureMeta> = {
  // ── joints ────────────────────────────────────────────────────────────────
  "joint-shoulder": {
    label: "Glenohumeral Joint",
    common: "Shoulder Joint",
    blurb:
      "The ball-and-socket joint of the shoulder; pain here often refers down the outer upper arm and is worse reaching overhead or behind the back.",
    kind: "joint",
    views: ["front", "back", "side"],
    paired: true,
  },
  "joint-elbow": {
    label: "Elbow Joint",
    common: "Elbow",
    blurb:
      "The hinge joint between the upper arm and forearm; pain is commonly felt on the inner or outer bony points and with gripping or twisting.",
    kind: "joint",
    views: ["front", "back", "side"],
    paired: true,
  },
  "joint-wrist": {
    label: "Radiocarpal Joint",
    common: "Wrist Joint",
    blurb:
      "The joint between the forearm bones and the small carpal bones; pain is typically worse bearing weight through the hand or with end-range bending.",
    kind: "joint",
    views: ["front", "back", "hand-palmar", "hand-dorsal"],
    paired: true,
  },
  "joint-hip": {
    label: "Acetabulofemoral Joint",
    common: "Hip Joint",
    blurb:
      "The deep ball-and-socket joint of the hip; true hip-joint pain is usually felt in the groin and can refer to the front of the thigh or the knee.",
    kind: "joint",
    views: ["front", "back", "side"],
    paired: true,
  },
  "joint-knee": {
    label: "Tibiofemoral Joint",
    common: "Knee Joint",
    blurb:
      "The main weight-bearing joint of the knee; pain may be felt along the joint line, behind the kneecap, or deep in the joint with stairs and squatting.",
    kind: "joint",
    views: ["front", "back", "side"],
    paired: true,
  },
  "joint-ankle": {
    label: "Talocrural Joint",
    common: "Ankle Joint",
    blurb:
      "The hinge joint between the leg bones and the talus; pain is usually felt at the front or sides of the ankle and is worse pushing off or on uneven ground.",
    kind: "joint",
    views: ["front", "back", "side", "foot-dorsal"],
    paired: true,
  },
  "joint-tmj": {
    label: "Temporomandibular Joint",
    common: "Jaw Joint",
    blurb:
      "The joint just in front of the ear where the jaw meets the skull; pain, clicking, or locking here is often worse chewing or yawning and can refer to the temple.",
    kind: "joint",
    views: ["front", "side", "face"],
    paired: true,
  },
  "joint-si": {
    label: "Sacroiliac Joint",
    common: "SI Joint",
    blurb:
      "The joint between the sacrum and pelvis low in the back; pain is felt in a small area beside the tailbone and can spread into the buttock or back of the thigh.",
    kind: "joint",
    views: ["back"],
    paired: true,
  },
  "joint-mcp": {
    label: "Metacarpophalangeal Joint",
    common: "Knuckle",
    blurb:
      "The large knuckle joint at the base of each finger; pain and swelling here are common with inflammatory arthritis and with jammed or sprained fingers.",
    kind: "joint",
    views: ["front", "hand-palmar", "hand-dorsal"],
    paired: true,
  },
  "joint-pip": {
    label: "Proximal Interphalangeal Joint",
    common: "Middle Finger Knuckle",
    blurb:
      "The middle joint of each finger; it commonly stiffens and enlarges with osteoarthritis and is slow to settle after a sprain.",
    kind: "joint",
    views: ["front", "hand-palmar", "hand-dorsal"],
    paired: true,
  },
  "joint-dip": {
    label: "Distal Interphalangeal Joint",
    common: "Fingertip Knuckle",
    blurb:
      "The joint closest to the fingernail; bony knobbly swelling here is a classic sign of finger osteoarthritis.",
    kind: "joint",
    views: ["front", "hand-palmar", "hand-dorsal"],
    paired: true,
  },
  "joint-mtp": {
    label: "Metatarsophalangeal Joint",
    common: "Toe Knuckle",
    blurb:
      "The joint at the base of each toe; the big-toe joint in particular is a common site for gout, bunion pain, and push-off pain when walking.",
    kind: "joint",
    views: ["foot-dorsal", "foot-plantar"],
    paired: true,
  },
  "joint-subtalar": {
    label: "Subtalar Joint",
    common: "Rearfoot Joint",
    blurb:
      "The joint below the ankle that lets the foot roll in and out; pain here is felt just below the ankle bones and is worse on uneven ground.",
    kind: "joint",
    views: ["foot-dorsal", "foot-plantar"],
    paired: true,
  },

  // ── spine segments ────────────────────────────────────────────────────────
  "spine-c": {
    label: "Cervical Spine",
    common: "Neck",
    blurb:
      "The seven vertebrae of the neck; pain here can be local or refer into the shoulder blade, arm, or head depending on the level involved.",
    kind: "bone",
    views: ["back", "side", "top-down", "face"],
    paired: false,
  },
  "spine-t": {
    label: "Thoracic Spine",
    common: "Mid Back",
    blurb:
      "The twelve vertebrae of the mid back that anchor the ribs; pain here is often felt between or below the shoulder blades and can wrap around the ribcage.",
    kind: "bone",
    views: ["back", "side"],
    paired: false,
  },
  "spine-l1": {
    label: "L1 Vertebral Segment",
    common: "Upper Lumbar Spine",
    blurb:
      "The first lumbar level at the top of the low back; irritation here can refer pain around the flank toward the groin.",
    kind: "bone",
    views: ["back", "side"],
    paired: false,
  },
  "spine-l2": {
    label: "L2 Vertebral Segment",
    common: "Upper Lumbar Spine",
    blurb:
      "The second lumbar level; nerve irritation here can refer into the front of the thigh.",
    kind: "bone",
    views: ["back", "side"],
    paired: false,
  },
  "spine-l3": {
    label: "L3 Vertebral Segment",
    common: "Mid Lumbar Spine",
    blurb:
      "The third lumbar level; nerve irritation here can refer to the front of the thigh and the inner knee.",
    kind: "bone",
    views: ["back", "side"],
    paired: false,
  },
  "spine-l4": {
    label: "L4 Vertebral Segment",
    common: "Lower Lumbar Spine",
    blurb:
      "The fourth lumbar level; the L4 nerve refers pain down the outer thigh, across the knee, and into the shin.",
    kind: "bone",
    views: ["back", "side"],
    paired: false,
  },
  "spine-l5": {
    label: "L5 Vertebral Segment",
    common: "Lower Lumbar Spine",
    blurb:
      "The fifth lumbar level and a very common disc site; the L5 nerve refers pain down the outer leg into the top of the foot and big toe.",
    kind: "bone",
    views: ["back", "side"],
    paired: false,
  },
  "spine-s": {
    label: "Sacral Spine",
    common: "Tailbone Area",
    blurb:
      "The fused triangular bone at the base of the spine; pain here is felt low and central and can accompany SI-joint or pelvic-floor problems.",
    kind: "bone",
    views: ["back", "side"],
    paired: false,
  },

  // ── bone landmarks ───────────────────────────────────────────────────────
  "bone-clavicle": {
    label: "Clavicle",
    common: "Collarbone",
    blurb:
      "The strut of bone across the top of the chest; pain and a step deformity after a fall on the shoulder suggest a fracture.",
    kind: "bone",
    views: ["front"],
    paired: true,
  },
  "bone-sternum": {
    label: "Sternum",
    common: "Breastbone",
    blurb:
      "The flat bone at the centre of the chest; localised tenderness that hurts to press is usually musculoskeletal rather than cardiac, but new chest pain always warrants care.",
    kind: "bone",
    views: ["front"],
    paired: false,
  },
  "bone-rib": {
    label: "Rib",
    common: "Rib",
    blurb:
      "The curved bones of the chest wall; a sharp, well-localised pain that is worse with a deep breath, cough, or press points to a rib or its cartilage.",
    kind: "bone",
    views: ["front"],
    paired: true,
  },
  "bone-patella": {
    label: "Patella",
    common: "Kneecap",
    blurb:
      "The small bone in front of the knee; pain around or behind it is common with stairs, prolonged sitting, and kneeling.",
    kind: "bone",
    views: ["front"],
    paired: true,
  },
  "bone-tibia-shaft": {
    label: "Tibial Shaft",
    common: "Shin Bone",
    blurb:
      "The front edge of the shin bone; a diffuse ache along the inner border with running is typical of shin splints, while a pinpoint spot can indicate a stress fracture.",
    kind: "bone",
    views: ["front"],
    paired: true,
  },
  "bone-calcaneus": {
    label: "Calcaneus",
    common: "Heel Bone",
    blurb:
      "The heel bone; pain underneath is often plantar fasciitis, pain at the back is usually the Achilles insertion, and squeeze pain from the sides can mean a stress fracture.",
    kind: "bone",
    views: ["back", "foot-dorsal", "foot-plantar"],
    paired: true,
  },
  "bone-metatarsal": {
    label: "Metatarsal",
    common: "Long Foot Bones",
    blurb:
      "The five long bones of the forefoot; a gradually worsening ache over one bone with activity is a common stress-fracture presentation.",
    kind: "bone",
    views: ["foot-dorsal", "foot-plantar"],
    paired: true,
  },
  "bone-scaphoid": {
    label: "Scaphoid",
    common: "Wrist Bone (Thumb Side)",
    blurb:
      "A small wrist bone at the base of the thumb; tenderness in the hollow there after a fall on an outstretched hand is a fracture until proven otherwise.",
    kind: "bone",
    views: ["hand-palmar", "hand-dorsal"],
    paired: true,
  },

  // ── nerves ───────────────────────────────────────────────────────────────
  "nerve-sciatic": {
    label: "Sciatic Nerve",
    common: "Sciatic Nerve",
    blurb:
      "The large nerve running from the low back through the buttock and down the back of the leg; irritation causes shooting or burning pain along that path, sometimes with numbness.",
    kind: "nerve",
    views: ["back", "side"],
    paired: true,
  },
  "nerve-median": {
    label: "Median Nerve",
    common: "Median Nerve",
    blurb:
      "The nerve that passes through the carpal tunnel at the wrist; compression causes tingling and numbness in the thumb, index, and middle fingers, often worse at night.",
    kind: "nerve",
    views: ["front", "hand-palmar"],
    paired: true,
  },
  "nerve-ulnar": {
    label: "Ulnar Nerve",
    common: "Funny-Bone Nerve",
    blurb:
      "The nerve behind the inner elbow that continues to the little-finger side of the hand; irritation causes tingling in the ring and little fingers and a weak grip.",
    kind: "nerve",
    views: ["front", "hand-palmar", "hand-dorsal"],
    paired: true,
  },
  "nerve-radial": {
    label: "Radial Nerve",
    common: "Radial Nerve",
    blurb:
      "The nerve that wraps the back of the upper arm to supply the wrist and finger extensors; irritation causes back-of-forearm pain and, if severe, a dropped wrist.",
    kind: "nerve",
    views: ["front", "hand-dorsal"],
    paired: true,
  },
  "nerve-peroneal": {
    label: "Common Peroneal Nerve",
    common: "Fibular Nerve",
    blurb:
      "The nerve that winds around the outside of the knee toward the top of the foot; compression there causes numbness over the foot and difficulty lifting it.",
    kind: "nerve",
    views: ["side", "foot-dorsal"],
    paired: true,
  },
  "nerve-tibial": {
    label: "Tibial Nerve",
    common: "Tibial Nerve",
    blurb:
      "The nerve that runs behind the inner ankle to the sole; compression there (tarsal tunnel) causes burning and tingling in the sole and toes.",
    kind: "nerve",
    views: ["side", "foot-plantar"],
    paired: true,
  },
  "nerve-plantar": {
    label: "Plantar Nerves",
    common: "Sole Nerves",
    blurb:
      "The branches supplying the sole of the foot; irritation between the third and fourth toes with a burning, pebble-in-the-shoe feeling suggests a Morton's neuroma.",
    kind: "nerve",
    views: ["foot-plantar"],
    paired: true,
  },

  // ── dermatomes (educational overlay only) ────────────────────────────────
  "derm-c5": {
    label: "C5 Dermatome",
    common: "C5 Skin Band",
    blurb:
      "The strip of skin over the outer shoulder and upper arm supplied by the C5 nerve root; numbness or shooting pain in this band points to that level in the neck.",
    kind: "dermatome",
    views: ["front"],
    paired: true,
  },
  "derm-c6": {
    label: "C6 Dermatome",
    common: "C6 Skin Band",
    blurb:
      "The band running down the thumb side of the forearm to the thumb, supplied by the C6 nerve root.",
    kind: "dermatome",
    views: ["front"],
    paired: true,
  },
  "derm-c7": {
    label: "C7 Dermatome",
    common: "C7 Skin Band",
    blurb:
      "The band down the back of the forearm to the middle finger, supplied by the C7 nerve root.",
    kind: "dermatome",
    views: ["front"],
    paired: true,
  },
  "derm-c8": {
    label: "C8 Dermatome",
    common: "C8 Skin Band",
    blurb:
      "The band along the little-finger side of the hand and forearm, supplied by the C8 nerve root.",
    kind: "dermatome",
    views: ["front"],
    paired: true,
  },
  "derm-t1": {
    label: "T1 Dermatome",
    common: "T1 Skin Band",
    blurb:
      "The band along the inner forearm above the elbow, supplied by the T1 nerve root.",
    kind: "dermatome",
    views: ["front"],
    paired: true,
  },
  "derm-l4": {
    label: "L4 Dermatome",
    common: "L4 Skin Band",
    blurb:
      "The band crossing the front of the thigh to the inner shin and inner ankle, supplied by the L4 nerve root.",
    kind: "dermatome",
    views: ["back"],
    paired: true,
  },
  "derm-l5": {
    label: "L5 Dermatome",
    common: "L5 Skin Band",
    blurb:
      "The band down the outer lower leg to the top of the foot and big toe, supplied by the L5 nerve root.",
    kind: "dermatome",
    views: ["back"],
    paired: true,
  },
  "derm-s1": {
    label: "S1 Dermatome",
    common: "S1 Skin Band",
    blurb:
      "The band down the back of the calf to the outer border and sole of the foot, supplied by the S1 nerve root.",
    kind: "dermatome",
    views: ["back"],
    paired: true,
  },

  // ── visceral quadrants ──────────────────────────────────────────────────
  "visc-ruq": {
    label: "Right Upper Quadrant",
    common: "Upper-Right Abdomen",
    blurb:
      "The area under the right lower ribs overlying the liver and gallbladder; pain here, especially after fatty food, can be gallbladder-related.",
    kind: "visceral",
    views: ["front"],
    paired: false,
  },
  "visc-luq": {
    label: "Left Upper Quadrant",
    common: "Upper-Left Abdomen",
    blurb:
      "The area under the left lower ribs overlying the stomach, spleen, and part of the pancreas.",
    kind: "visceral",
    views: ["front"],
    paired: false,
  },
  "visc-rlq": {
    label: "Right Lower Quadrant",
    common: "Lower-Right Abdomen",
    blurb:
      "The area over the appendix and right ovary/tube; pain that starts near the navel and settles here is a classic appendicitis pattern and needs prompt care.",
    kind: "visceral",
    views: ["front"],
    paired: false,
  },
  "visc-llq": {
    label: "Left Lower Quadrant",
    common: "Lower-Left Abdomen",
    blurb:
      "The area over the sigmoid colon and left ovary/tube; a common site for diverticulitis pain in older adults.",
    kind: "visceral",
    views: ["front"],
    paired: false,
  },
  "visc-epigastric": {
    label: "Epigastrium",
    common: "Upper-Middle Abdomen",
    blurb:
      "The area just below the breastbone overlying the stomach and pancreas; burning here after meals suggests reflux or an ulcer, but it can also be referred cardiac pain.",
    kind: "visceral",
    views: ["front"],
    paired: false,
  },
  "visc-suprapubic": {
    label: "Suprapubic Region",
    common: "Lower-Middle Abdomen",
    blurb:
      "The area just above the pubic bone overlying the bladder and uterus; pain with urinary urgency or burning suggests a bladder cause.",
    kind: "visceral",
    views: ["front"],
    paired: false,
  },

  // ── soft tissue / other ─────────────────────────────────────────────────
  "fascia-plantar": {
    label: "Plantar Fascia",
    common: "Arch Band",
    blurb:
      "The thick band along the sole from the heel to the ball of the foot; sharp heel pain with the first steps in the morning is the hallmark of plantar fasciitis.",
    kind: "skin",
    views: ["foot-plantar"],
    paired: true,
  },
  "tissue-skin": {
    label: "Skin",
    common: "Skin",
    blurb:
      "The skin surface itself; burning, stinging, or raw pain confined to the skin points to a superficial cause such as a rash, shingles, or a friction injury.",
    kind: "skin",
    views: ["top-down", "face"],
    paired: false,
  },
  "tissue-tendon": {
    label: "Tendon / Connective Tissue",
    common: "Tendon",
    blurb:
      "The cords that attach muscle to bone; tendon pain is localised over the cord, worse loading the muscle, and often tender to pinch.",
    kind: "skin",
    views: ["hand-palmar", "hand-dorsal", "foot-dorsal", "foot-plantar"],
    paired: true,
  },
};

/** Quality chips offered when the tapped structure is of this kind (D-PAIN-10).
 *  These EXTEND, not replace, SYMPTOM_CHARACTERS. */
export const QUALITY_BY_KIND: Record<PainStructureKind, string[]> = {
  muscle: ["aching", "cramping", "tight", "sore", "spasming", "throbbing"],
  joint: ["stiff", "locking", "grinding", "swollen", "unstable", "catching"],
  bone: ["deep ache", "tender to touch", "sharp on impact", "night pain"],
  nerve: ["shooting", "electric", "pins and needles", "numb", "burning"],
  dermatome: ["shooting", "electric", "pins and needles", "numb", "burning"],
  visceral: ["cramping", "gnawing", "pressure", "colicky", "bloated"],
  skin: ["burning", "itching", "raw", "stinging", "tender"],
};
